import { Typography, Row, Col, Button, Empty } from 'antd';
import ProductCard from '../ProductCard';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { setIsDrawerOpen } from '../../../store/workflows/slice';
import { setIsStylingWorkflowDrawerOpen } from '../../../store/stylist/slice';
import { useSearchProductsInfiniteQuery } from '../../../store/products/productsApi';
import ProductCardSkeleton from '../ProductCard/Skeleton';
import { selectActiveFilters } from '../../../store/products/slice';
import { useFetchOnListEnd } from '@admin-panel/hooks';
import { ProductDTOType } from '../../../schemas/product';
import { useProductsContext } from '../context';

import * as Styles from './styles';

function explodeProductsByColor(products: Array<ProductDTOType>) {
  return products.flatMap(product => {
    const colorMap = new Map();
    product.variants.forEach(variant => {
      if (variant.color && !colorMap.has(variant.color)) {
        colorMap.set(variant.color, variant);
      }
    });
    if (colorMap.size === 0) return [{ product, color: null, variant: product.variants[0] }];
    return Array.from(colorMap.entries()).map(
      ([color, variant]) =>
        ({
          product,
          color,
          variant,
        }) as {
          product: ProductDTOType;
          color: string | null;
          variant: ProductDTOType['variants'][number];
        },
    );
  });
}

const List = () => {
  const dispatch = useAppDispatch();

  const activeFilters = useAppSelector(selectActiveFilters);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSearchProductsInfiniteQuery({
      filters: activeFilters,
    });

  const { usage } = useProductsContext();

  const allProducts = data?.pages.flatMap(page => page.results) || [];

  const explodedProducts = explodeProductsByColor(allProducts);

  const { lastItemRef } = useFetchOnListEnd(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  return (
    <Styles.Wrapper>
      <Styles.Header>
        <Typography.Paragraph
          strong
          style={{
            margin: 0,
          }}
        >
          Products List
        </Typography.Paragraph>
        {usage === 'TAGGING' && (
          <Button
            type="primary"
            onClick={() => {
              dispatch(setIsDrawerOpen(true));
            }}
          >
            CREATE A NEW TAGGING JOB
          </Button>
        )}
        {usage === 'STYLING' && (
          <Button
            type="primary"
            onClick={() => {
              dispatch(setIsStylingWorkflowDrawerOpen(true));
            }}
          >
            CREATE A NEW STYLING JOB
          </Button>
        )}
      </Styles.Header>

      <Styles.Content>
        {isFetching && !isLoading && !isFetchingNextPage && (
          <Row gutter={[20, 24]} justify="start">
            {Array.from({ length: 6 }).map((_, i) => (
              <Col key={i} xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
                <ProductCardSkeleton />
              </Col>
            ))}
          </Row>
        )}

        {isLoading ? (
          <Row gutter={[20, 24]} justify="start">
            {Array.from({ length: 6 }).map((_, i) => (
              <Col key={i} xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
                <ProductCardSkeleton />
              </Col>
            ))}
          </Row>
        ) : explodedProducts.length === 0 ? (
          <div style={{ padding: 40 }}>
            <Empty description="No products found" />
          </div>
        ) : (
          <Row gutter={[20, 24]} justify="start">
            {explodedProducts.map(({ product, color, variant }, idx) => (
              <Col
                key={product.id + '-' + (color || 'default') + variant.id}
                xs={24}
                sm={24}
                md={12}
                lg={8}
                xl={8}
                xxl={8}
                ref={idx === explodedProducts.length - 1 && hasNextPage ? lastItemRef : undefined}
              >
                <ProductCard data={product} variant={variant} />
              </Col>
            ))}
            {isFetchingNextPage &&
              Array.from({ length: 3 }).map((_, i) => (
                <Col key={`skeleton-next-${i}`} xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
                  <ProductCardSkeleton />
                </Col>
              ))}
          </Row>
        )}
      </Styles.Content>
    </Styles.Wrapper>
  );
};

export default List;
